// Small shared pieces: avatars, pills, type chips, milestone diamond, status.
import type { ActivityStatus, ActivityType, PersonSummary } from '@dpw/shared';
import { STATUS_LABELS, TYPE_LABELS } from '@dpw/shared';
import type { ReactNode } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { ApiError } from '@dpw/shared';

const initialsOf = (name: string) => name.replace(/^dr\.?\s+/i, '').split(/\s+/).map(p => p[0]).filter(Boolean).slice(0, 2).join('').toUpperCase();

export function Avatar({ p, lg }: { p: { name: string; initials?: string | null }; lg?: boolean }) {
  return <span className={`who2${lg ? ' lg' : ''}`} title={p.name}>{p.initials || initialsOf(p.name)}</span>;
}

export function Owners({ owners }: { owners: PersonSummary[] }) {
  if (!owners.length) return <span className="xs mut">No owner</span>;
  return <span className="stack">{owners.slice(0, 3).map(o => <Avatar key={o.id} p={o} />)}{owners.length > 3 && <span className="who2">+{owners.length - 3}</span>}</span>;
}

const TY_CLASS: Record<ActivityType, string> = { meeting: 'meet', workshop: 'work', deliverable: 'deliv', task: 'task' };
export const TypeChip = ({ type }: { type: ActivityType }) => <span className={`ty ${TY_CLASS[type]}`}><i />{TYPE_LABELS[type]}</span>;
export const tyColor = (t: ActivityType | 'reminder') => ({ meeting: 'var(--t-meet)', workshop: 'var(--t-work)', deliverable: 'var(--t-deliv)', task: 'var(--t-dead)', reminder: 'var(--t-rem)' })[t];
export const tyBg = (t: ActivityType | 'reminder') => ({ meeting: 'var(--t-meet-bg)', workshop: 'var(--t-work-bg)', deliverable: 'var(--t-deliv-bg)', task: 'var(--t-dead-bg)', reminder: 'var(--t-rem-bg)' })[t];

export function StatusPill({ status, overdue }: { status: ActivityStatus; overdue?: boolean }) {
  if (overdue) return <span className="pl red">Overdue</span>;
  const cls = { not_started: 'pl', in_progress: 'pl amb', at_risk: 'pl red', blocked: 'pl red', done: 'pl grn' }[status];
  return <span className={cls}>{STATUS_LABELS[status]}</span>;
}

export const Diamond = ({ sm, title = 'Milestone' }: { sm?: boolean; title?: string }) => <span className={`diamond${sm ? ' sm' : ''}`} title={title} aria-label={title} />;

export function Progress({ value, color = 'var(--ink)' }: { value: number; color?: string }) {
  return <span className="pb"><i style={{ width: `${Math.max(0, Math.min(100, value))}%`, background: color }} /></span>;
}

export function Panel({ title, right, children, className = '' }: { title?: ReactNode; right?: ReactNode; children: ReactNode; className?: string }) {
  return (
    <div className={`panel ${className}`}>
      {(title || right) && <div className="sh" style={{ marginBottom: 6 }}><b>{title}</b>{right}</div>}
      {children}
    </div>
  );
}

export function Toggle({ on, onChange, label }: { on: boolean; onChange: (v: boolean) => void; label: string }) {
  return <button type="button" className={`tog${on ? ' on' : ''}`} role="switch" aria-checked={on} aria-label={label} onClick={() => onChange(!on)} />;
}

export function Loading() { return <div className="rowc xs mut" style={{ padding: 16 }}><span className="spin" />Loading</div>; }

/** Errors in plain words. A version conflict ("This was changed by X") offers Reload instead of overwriting. */
export function ErrorNote({ error, onReload }: { error: unknown; onReload?: () => void }) {
  const qc = useQueryClient();
  const stale = error instanceof ApiError && error.code === 'stale_version';
  return (
    <div className="err" role="alert">
      {error instanceof Error ? error.message : 'Something went wrong.'}
      {stale && <> <button type="button" className="tlink" onClick={() => { void qc.invalidateQueries(); onReload?.(); }}>Reload</button></>}
    </div>
  );
}

/** A track's status (worked out from its activities): label and colour token. */
export const HEALTH: Record<'on_track' | 'watch' | 'at_risk', [string, string]> = { on_track: ['On track', 'grn-tx'], watch: ['Watch', 'amb-tx'], at_risk: ['At risk', 'red-tx'] };
